import axios from 'axios';
import { apiService } from './apiService';
import { MARKETPLACE_ENABLED } from '../config/featureFlags';
import { ensureFreshAitryxCoreToken } from './aitryxCoreTokenService';
import { config } from '../config/environment';
import type { InclusionItem } from '../PartnerMarketplace/inclusionItem';
import {
  PricingConflictError,
  type CustomerMicrosoftLicencePricing,
  type CustomerMicrosoftLicenceSetting,
  type MicrosoftLicenceReference,
  type PartnerMicrosoftLicencePricing,
  type TermPrices,
} from '../PartnerMarketplace/microsoftLicencePricing';

// Marketplace API URL from centralized config
const MARKETPLACE_API_URL = config.api.marketplaceUrl;

let microsoftLicenceReference: Promise<MicrosoftLicenceReference> | null = null;

/** The function's own message when it sent one; a 409 means someone else saved first. */
function pricingError(error: unknown, message: string): Error {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  const serverMessage = axios.isAxiosError(error) ? (error.response?.data as { error?: string } | undefined)?.error : undefined;
  if (status === 409) {
    return new PricingConflictError(serverMessage || 'These prices were changed somewhere else since you opened them.');
  }
  return new Error(serverMessage ? `${message}: ${serverMessage}` : message);
}

/**
 * Creates an authenticated axios instance for the marketplace function app
 */
async function getMarketplaceClient() {
  const token = await ensureFreshAitryxCoreToken();
  return axios.create({
    baseURL: MARKETPLACE_API_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
}

// Marketplace types (matching aitryx-customer and aitryx-core interfaces)
export interface MarketplaceSoftware {
  id: string;
  name: string;
  vendor: string;
  category: string;
  price: number;
  pricingType: 'perUser' | 'lumpSum';
  term: 0 | 6 | 12 | 18 | 24 | 36;
  billingCycle: 'monthly' | 'annual';
  description: string;
  image?: string;
  colour?: string;
  capacity?: string;
  popular?: boolean;
  enabled?: boolean;
  orderTag?: 'hardware_orders___returns' | 'software_orders' | 'other_orders';
  /** Display order for sorting within domain (lower numbers first) */
  displayOrder?: number;
  /** Optional sub-category for filtering (Productivity/Security for software) */
  subCategory?: string;
  /** Source of the product: dickerdata, ingram, telstra-distributor, manual */
  source?: string;
  /** SKU for distributor-sourced products */
  sku?: string;
  /** Vendor part number from the distributor feed. Ingram calls this VPN. */
  vendorPartNumber?: string;
  /** Stock on hand */
  soh?: number;
  /** Buy price from distributor (cost price) */
  buyPrice?: number;
  /** Markup percentage applied */
  markupPercent?: number;
  /** RRP from distributor */
  rrpExTax?: number;
  /** Per billing/commitment combination pricing (keyed by "billingCycle_term") */
  termPricing?: Record<string, number>;
  /** Vendor preset ID (e.g. "microsoft") controlling locked term options */
  vendorPreset?: string;
  /** Graph `subscribedSku.skuId` of the Microsoft licence a Microsoft plan sells. */
  microsoftSkuId?: string;
  /** Apps included grid (M365). Each item: { name, image? }. */
  inclusionApps?: Array<{ name: string; image?: string }>;
  /** Plan inclusions for software (legacy string[] or rich InclusionItem[]). */
  planInclusions?: string[] | InclusionItem[];
  /** Features list (legacy string[] or rich InclusionItem[]). */
  features?: string[] | InclusionItem[];
}

export interface MarketplaceHardware {
  id: string;
  name: string;      // Changed from 'model' to match customer expectation
  brand: string;     // Changed from 'manufacturer' to match customer expectation
  category: string;
  // Optional sub-category for more granular classification (e.g. Desktop/Laptop for computers,
  // Firewall/Router for networking, Handset/Tablet for mobile, Mobile/Data for Telstra)
  subCategory?: string;
  price: number;
  pricingType: 'perUser' | 'lumpSum';
  term: 0 | 6 | 12 | 18 | 24 | 36;
  billingCycle: 'monthly' | 'annual';
  description: string;
  specs: string[];
  image?: string;
  colour?: string;
  capacity?: string;
  /** Available colour options for mobile-hardware products (max 5). Customers select at checkout. */
  availableColours?: string[];
  /** Plan inclusions for Telstra products (legacy string[] or rich InclusionItem[]). */
  planInclusions?: string[] | InclusionItem[];
  /** Features for computers and unified-collaboration products (legacy string[] or rich InclusionItem[], max 10). */
  features?: string[] | InclusionItem[];
  popular?: boolean;
  enabled?: boolean;
  orderTag?: 'hardware_orders___returns' | 'software_orders' | 'other_orders';
  /** Domain for procurement routing */
  domain?: string;
  /** Source of the product: dickerdata, ingram, telstra-distributor, manual */
  source?: string;
  /** SKU for distributor-sourced products */
  sku?: string;
  /** Vendor part number from the distributor feed. Ingram calls this VPN. */
  vendorPartNumber?: string;
  /** Stock on hand */
  soh?: number;
  /** Buy price from distributor (cost price) */
  buyPrice?: number;
  /** Markup percentage applied */
  markupPercent?: number;
  /** RRP from distributor */
  rrpExTax?: number;
  /** Device eligibility for DRO (mobile-hardware only) */
  deviceEligibility?: string;
  /** Telstra product type (telstra domain only) */
  telstraProductType?: string;
  /** Display order for sorting within domain (lower numbers first) */
  displayOrder?: number;
}

export interface MarketplaceConsulting {
  id: string;
  name: string;
  brand: string;
  category: string;
  // Optional sub-category for consulting (e.g. Security/Strategy/Architecture)
  subCategory?: string;
  price: number;
  pricingType: 'perUser' | 'lumpSum';
  term: 0 | 6 | 12 | 18 | 24 | 36;
  billingCycle: 'monthly' | 'annual';
  description: string;
  specs?: string[];
  image?: string;
  colour?: string;
  capacity?: string;
  popular?: boolean;
  enabled?: boolean;
  orderTag?: 'hardware_orders___returns' | 'software_orders' | 'other_orders';
  /** Custom questions for consulting products (max 5) */
  customQuestions?: Array<{
    id: string;        // unique identifier
    label: string;     // question text
    required: boolean; // whether answer is required
  }>;
}

export interface MarketplaceConfig {
  software: MarketplaceSoftware[];
  hardware: MarketplaceHardware[];
  updatedAt: string;
  updatedBy: string;
  version: string;
  isCustomized: boolean;
}

export interface SaveMarketplaceConfigRequest {
  software?: MarketplaceSoftware[];
  hardware?: MarketplaceHardware[];
}

export interface MarketplaceItemRequest {
  itemType: string; // "software" or "hardware"
  software?: MarketplaceSoftware;
  hardware?: MarketplaceHardware;
}

/**
 * MarketplaceService for partner portal
 * Allows partners to configure marketplace catalogs for their customers
 */
export class MarketplaceService {
  
  /**
   * Get marketplace catalog for a specific customer
   */
  async getMarketplaceCatalog(customerId: string): Promise<MarketplaceConfig> {
    try {
      if (!MARKETPLACE_ENABLED) {
        return {
          software: [],
          hardware: [],
          updatedAt: new Date().toISOString(),
          updatedBy: 'system',
          version: '1.0',
          isCustomized: false,
        };
      }
      // Use partner-facing endpoint secured with Azure AD; API returns { success, data: MarketplaceConfig }
      const response = await apiService.get<MarketplaceConfig>(`/marketplace/partner/${customerId}/catalog`);

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to load marketplace catalog');
      }
    } catch (error) {
      throw error;
    }
  }

  // ===== Bulk Import & Catalogue APIs =====

  async uploadImport(file: File): Promise<{ batch: any; preview: any[] }> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    const form = new FormData();
    form.append('file', file);
    // Let Axios set the multipart boundary automatically (do NOT set Content-Type manually)
    const response = await apiService.http.post(`/marketplace/partner/imports`, form);
    const data = response.data;
    if (!data?.success) throw new Error(data?.error || 'Import failed');
    return data.data;
  }

  async uploadImportJson(payload: { fileName: string; rows: Array<{ productCategory: string; externalId: string; description: string; supply: string; rrp: string; lifecycle: string }> }): Promise<{ batch: any; preview: any[] }> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    const response = await apiService.http.post(`/marketplace/partner/imports`, payload, {
      headers: { 'Content-Type': 'application/json' },
    });
    const data = response.data;
    if (!data?.success) throw new Error(data?.error || 'Import failed');
    return data.data;
  }

  async getImportBatch(batchId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    return (await apiService.get<any>(`/marketplace/partner/imports/${batchId}`)).data;
  }

  async listImportedItems(batchId: string, params: { search?: string; category?: string; supply?: string; onlySelectable?: boolean; page?: number; pageSize?: number } = {}) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    return await apiService.getPaginated<any>(`/marketplace/partner/imports/${batchId}/items`, params);
  }

  async publishImport(batchId: string, payload: { name: string; notes?: string; externalIds: string[] }) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    return (await apiService.post<any>(`/marketplace/partner/imports/${batchId}/publish`, payload)).data;
  }

  async listSnapshots(params: { page?: number; pageSize?: number } = {}) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    return await apiService.getPaginated<any>(`/marketplace/partner/snapshots`, params);
  }

  async getSnapshotItems(snapshotId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    return (await apiService.get<any>(`/marketplace/partner/snapshots/${snapshotId}/items`)).data;
  }

  /**
   * Assign a catalogue (snapshot) to a customer.
   * Calls the backend AssignCatalogToCustomer endpoint which:
   * 1. Copies the catalogue to the customer's blob container
   * 2. Triggers real-time sync via Event Relay
   */
  async assignCatalogToCustomer(snapshotId: string, customerId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    
    const client = await getMarketplaceClient();
    await client.post(`/catalog/assign/${customerId}`, { snapshotId });
    return true;
  }

  /**
   * Get the currently assigned catalogue for a customer (partner-facing).
   * Returns the full customer catalog including products.
   */
  async getCustomerAssignedCatalog(customerId: string): Promise<{
    customerId: string;
    assignedSnapshotId?: string;
    updatedAt?: string;
    hardware?: any[];
    software?: any[];
    consulting?: any[];
    computerOnboardingChargeAud?: number | null;
    computerOnboardingChargeLabel?: string | null;
    deviceEnrolmentAvailable?: boolean;
    microsoftLicencesEnabled?: boolean | null;
  } | null> {
    if (!MARKETPLACE_ENABLED) return null;
    
    try {
      const client = await getMarketplaceClient();
      // Use the partner-facing endpoint with customerId in the path
      const response = await client.get(`/catalog/customer/${customerId}`);
      return response.data || null;
    } catch (error) {
      throw pricingError(error, "Couldn't load this customer's marketplace");
    }
  }

  /**
   * Per-unit computer onboarding charge (AUD) for this customer; shown on customer checkout when cart includes computers.
   */
  async updateCustomerComputerOnboarding(
    customerId: string,
    payload: {
      computerOnboardingChargeAud: number | null;
      computerOnboardingChargeLabel: string | null;
    }
  ): Promise<void> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    const client = await getMarketplaceClient();
    await client.patch(`/catalog/customer/${customerId}/computer-onboarding`, {
      computerOnboardingChargeAud: payload.computerOnboardingChargeAud,
      computerOnboardingChargeLabel: payload.computerOnboardingChargeLabel,
    });
  }

  /**
   * Per-customer mobile hardware settings (currently the Device Enrolment Service toggle).
   * When enabled, the customer app surfaces a DES Yes/No prompt on mobile hardware orders.
   */
  async updateCustomerMobileHardwareSettings(
    customerId: string,
    payload: { deviceEnrolmentAvailable: boolean }
  ): Promise<void> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    const client = await getMarketplaceClient();
    await client.patch(`/catalog/customer/${customerId}/mobile-hardware-settings`, {
      deviceEnrolmentAvailable: payload.deviceEnrolmentAvailable,
    });
  }

  async updateCustomerMicrosoftLicences(customerId: string, enabled: boolean): Promise<void> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    const client = await getMarketplaceClient();
    await client.patch(`/catalog/customer/${customerId}/microsoft-licences`, { enabled });
  }

  /** The built-in Microsoft licence reference. Loaded once per session; a failed load is retried on the next call. */
  getMicrosoftLicenceReference(): Promise<MicrosoftLicenceReference> {
    if (!MARKETPLACE_ENABLED) return Promise.reject(new Error('MARKETPLACE_DISABLED'));
    if (!microsoftLicenceReference) {
      microsoftLicenceReference = (async () => {
        try {
          const client = await getMarketplaceClient();
          const response = await client.get<MicrosoftLicenceReference>('/catalog/partner/microsoft-licences');
          return response.data;
        } catch (error) {
          microsoftLicenceReference = null;
          throw pricingError(error, "Couldn't load Microsoft's licence list");
        }
      })();
    }
    return microsoftLicenceReference;
  }

  async getPartnerMicrosoftLicencePricing(): Promise<PartnerMicrosoftLicencePricing> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    try {
      const client = await getMarketplaceClient();
      const response = await client.get<PartnerMicrosoftLicencePricing>('/catalog/partner/microsoft-licence-pricing');
      return response.data;
    } catch (error) {
      throw pricingError(error, "Couldn't load your Microsoft licence prices");
    }
  }

  async savePartnerMicrosoftLicencePricing(pricing: {
    version: string | null;
    defaultDistributor: string;
    distributors: Record<string, { prices: Record<string, TermPrices> }>;
    descriptions?: Record<string, string>;
  }): Promise<PartnerMicrosoftLicencePricing> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    try {
      const client = await getMarketplaceClient();
      const response = await client.put<PartnerMicrosoftLicencePricing>('/catalog/partner/microsoft-licence-pricing', pricing);
      return response.data;
    } catch (error) {
      throw pricingError(error, "Couldn't save your Microsoft licence prices");
    }
  }

  async getCustomerMicrosoftLicencePricing(customerId: string): Promise<CustomerMicrosoftLicencePricing> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    try {
      const client = await getMarketplaceClient();
      const response = await client.get<CustomerMicrosoftLicencePricing>(`/catalog/customer/${customerId}/microsoft-licence-pricing`);
      return response.data;
    } catch (error) {
      throw pricingError(error, "Couldn't load this customer's Microsoft licence prices");
    }
  }

  async saveCustomerMicrosoftLicencePricing(customerId: string, pricing: {
    version: string | null;
    defaultDistributor: string | null;
    licences: Record<string, CustomerMicrosoftLicenceSetting>;
  }): Promise<CustomerMicrosoftLicencePricing> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    try {
      const client = await getMarketplaceClient();
      const response = await client.put<CustomerMicrosoftLicencePricing>(`/catalog/customer/${customerId}/microsoft-licence-pricing`, pricing);
      return response.data;
    } catch (error) {
      throw pricingError(error, "Couldn't save this customer's Microsoft licence prices");
    }
  }

  /**
   * @deprecated Use assignCatalogToCustomer instead
   */
  async assignSnapshot(snapshotId: string, customerTenantId: string) {
    return this.assignCatalogToCustomer(snapshotId, customerTenantId);
  }

  /**
   * @deprecated Unassign is not currently supported - assign a different catalogue instead
   */
  async unassignSnapshot(_snapshotId: string, _customerTenantId: string) {
    return false;
  }

  async archiveSnapshot(snapshotId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    return (await apiService.post<any>(`/marketplace/partner/snapshots/${snapshotId}/archive`)).success;
  }

  // ===== Blob-based partner snapshots (via marketplace function app) =====
  async createPartnerSnapshotBlob(payload: { snapshotId?: string; name: string; notes?: string; hardware: MarketplaceHardware[]; software: MarketplaceSoftware[]; consulting?: MarketplaceConsulting[] }) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    
    const client = await getMarketplaceClient();
    const response = await client.post('/catalog/partner/snapshots', payload);
    return response.data;
  }

  async listPartnerSnapshotsBlob(): Promise<Array<{ snapshotId: string; name: string; createdAtUtc: string; itemCount: number }>> {
    if (!MARKETPLACE_ENABLED) return [];
    
    const client = await getMarketplaceClient();
    const response = await client.get('/catalog/partner/snapshots');
    const snapshots = response.data?.snapshots || [];
    
    // Transform to the expected format
    return snapshots.map((s: any) => ({
      snapshotId: s.snapshotId as string,
      name: s.name as string,
      createdAtUtc: s.createdAtUtc as string,
      itemCount: ((s.hardwareCount || 0) + (s.softwareCount || 0) + (s.consultingCount || 0)) as number
    }));
  }

  async getPartnerSnapshotBlob(snapshotId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    
    const client = await getMarketplaceClient();
    const response = await client.get(`/catalog/partner/snapshots/${snapshotId}`);
    const snapshot = response.data;
    
    return {
      snapshotId: snapshot.snapshotId,
      name: snapshot.name,
      notes: snapshot.notes,
      hardware: (snapshot.hardware || []).map((h: any) => ({
        id: h.id,
        name: h.name,
        brand: h.brand || 'Unknown',
        category: h.category || 'General',
        price: h.price || 0,
        pricingType: h.pricingType || 'lumpSum',
        term: h.term ?? 12,
        billingCycle: h.billingCycle || 'monthly',
        description: h.description || '',
        specs: h.specs || [],
        image: h.image,
        colour: h.colour,
        capacity: h.capacity,
        // Available colour options for mobile-hardware (customer selects at checkout)
        availableColours: h.availableColours,
        popular: h.popular,
        enabled: h.enabled !== false,
        // Domain for procurement routing
        domain: h.domain,
        // Extra fields from distributor
        sku: h.sku,
        vendorPartNumber: h.vendorPartNumber,
        source: h.source,
        soh: h.soh,
        buyPrice: h.buyPrice,
        markupPercent: h.markupPercent,
        rrpExTax: h.rrpExTax,
        // Telstra-specific fields
        telstraProductType: h.telstraProductType,
        // Plan inclusions for Telstra products (displayed as bullet points)
        planInclusions: h.planInclusions,
        // Features for computers products (displayed as bullet points)
        features: h.features,
        // Mobile Hardware device eligibility for DRO
        deviceEligibility: h.deviceEligibility,
        // Display order for sorting within domain (lower numbers first)
        displayOrder: h.displayOrder,
        vendorPreset: h.vendorPreset,
        termPricing: h.termPricing,
        microsoftSkuId: h.microsoftSkuId,
        // Sub-category for filtering (e.g., Laptop/Desktop for computers)
        subCategory: h.subCategory,
        // Server-hydrated canonical enrichment attributes for distributor SKUs
        enrichmentAttributes: h.enrichmentAttributes,
      })),
      software: (snapshot.software || []).map((s: any) => ({
        id: s.id,
        name: s.name,
        vendor: s.brand || s.vendor || 'Unknown',  // Prefer brand (backend field) over vendor (legacy)
        brand: s.brand || s.vendor || 'Unknown',   // Also expose as brand for consistency
        category: s.category || 'General',
        price: s.price || 0,
        pricingType: s.pricingType || 'perUser',
        term: s.term ?? 12,
        billingCycle: s.billingCycle || 'monthly',
        description: s.description || '',
        image: s.image,
        colour: s.colour,
        capacity: s.capacity,
        popular: s.popular,
        enabled: s.enabled !== false,
        domain: s.domain || 'software',
        source: s.source || 'manual',
        sku: s.sku,
        vendorPartNumber: (s as any).vendorPartNumber,
        soh: s.soh,
        buyPrice: s.buyPrice,
        markupPercent: s.markupPercent,
        rrpExTax: s.rrpExTax,
        // Display order for sorting within domain (lower numbers first)
        displayOrder: s.displayOrder,
        // Sub-category for filtering (Productivity/Security for software)
        subCategory: s.subCategory,
        // Per billing/commitment combination pricing
        termPricing: s.termPricing,
        vendorPreset: s.vendorPreset,
        // Every snapshot save rebuilds products from this mapping, so an unmapped field
        // is erased the next time anything in the catalogue is saved.
        microsoftSkuId: s.microsoftSkuId,
      })),
      consulting: (snapshot.consulting || []).map((c: any) => ({
        id: c.id,
        name: c.name,
        brand: c.brand || 'Unknown',
        category: c.category || 'consulting',
        price: c.price || 0,
        pricingType: c.pricingType || 'lumpSum',
        term: c.term ?? 12,
        billingCycle: c.billingCycle || 'monthly',
        description: c.description || '',
        specs: c.specs || [],
        image: c.image,
        colour: c.colour,
        capacity: c.capacity,
        popular: c.popular,
        enabled: c.enabled !== false,
        domain: c.domain || 'consulting',
        source: c.source || 'manual',
        vendorPartNumber: (c as any).vendorPartNumber,
        // Display order for sorting within domain (lower numbers first)
        displayOrder: c.displayOrder,
      }))
    };
  }

  async deletePartnerSnapshotBlob(snapshotId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    
    const client = await getMarketplaceClient();
    await client.delete(`/catalog/partner/snapshots/${snapshotId}`);
    return true;
  }

  /**
   * Apply a partner snapshot (catalogue) to a specific customer.
   * 
   * Uses the marketplace function's assign endpoint which:
   * 1. Reads the snapshot directly from blob storage (preserving ALL fields)
   * 2. Copies hardware/software/consulting to customer catalog
   * 3. Tracks customer assignment in the snapshot for daily sync propagation
   * 
   * This is cleaner than the old approach which manually mapped fields
   * and went through aitryx-core (which had an incomplete model that stripped fields).
   */
  async applySnapshotToCustomer(snapshotId: string, customerId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    
    // Use marketplace function endpoint - this reads the snapshot directly from blob
    // and preserves ALL fields (soh, features, displayOrder, etc.)
    const client = await getMarketplaceClient();
    const response = await client.post(`/catalog/assign/${customerId}`, {
      snapshotId
    });
    
    return response.data;
  }

  /**
   * Reset a customer's marketplace catalog to an empty state.
   * Note: Backend marks saved catalogs as customized; this will result
   * in an empty customized catalog (no items), which effectively clears
   * what the customer sees.
   */
  async resetCustomerCatalog(customerId: string) {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    return await this.saveMarketplaceCatalog(customerId, { software: [], hardware: [] });
  }

  /**
   * Save marketplace catalog for a specific customer
   */
  async saveMarketplaceCatalog(customerId: string, config: SaveMarketplaceConfigRequest): Promise<MarketplaceConfig> {
    try {
      if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
      // Use partner-facing endpoint secured with Azure AD; API returns { success, data: MarketplaceConfig }
      const response = await apiService.post<MarketplaceConfig>(`/marketplace/partner/${customerId}/catalog`, config);

      if (response.success && response.data) {
        return response.data;
      } else {
        throw new Error(response.error || 'Failed to save marketplace catalog');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Add a single item to the marketplace catalog
   */
  async addMarketplaceItem(customerId: string, itemRequest: MarketplaceItemRequest): Promise<MarketplaceConfig> {
    try {
      if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
      const response = await apiService.post<{ data: MarketplaceConfig }>(`/marketplace/${customerId}/catalog/item`, itemRequest);
      
      if (response.success && response.data?.data) {
        return response.data.data;
      } else {
        throw new Error(response.error || 'Failed to add marketplace item');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a marketplace item
   */
  async deleteMarketplaceItem(customerId: string, itemType: string, itemId: string): Promise<MarketplaceConfig> {
    try {
      if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
      const response = await apiService.delete<{ data: MarketplaceConfig }>(`/marketplace/${customerId}/catalog/${itemType}/${itemId}`);
      
      if (response.success && response.data?.data) {
        return response.data.data;
      } else {
        throw new Error(response.error || 'Failed to delete marketplace item');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Restore default marketplace catalog for a customer
   */
  async restoreDefaultCatalog(customerId: string): Promise<MarketplaceConfig> {
    try {
      if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
      // Delete the current custom configuration to revert to defaults
      const response = await apiService.delete<{ data: MarketplaceConfig }>(`/marketplace/${customerId}/catalog`);
      
      if (response.success) {
        // Return the default configuration by getting it again
        return await this.getMarketplaceCatalog(customerId);
      } else {
        throw new Error(response.error || 'Failed to restore default catalog');
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * Preview marketplace configuration
   * Returns what the customer would see in their marketplace
   */
  async previewMarketplace(customerId: string): Promise<{
    enabledSoftware: MarketplaceSoftware[];
    enabledHardware: MarketplaceHardware[];
    totalItems: number;
  }> {
    try {
      const config = await this.getMarketplaceCatalog(customerId);
      
      const enabledSoftware = config.software.filter(item => item.enabled !== false);
      const enabledHardware = config.hardware.filter(item => item.enabled !== false);
      
      return {
        enabledSoftware,
        enabledHardware,
        totalItems: enabledSoftware.length + enabledHardware.length
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Reorder products within a catalogue snapshot. Sends only a delta
   * of (id, displayOrder) entries per domain bucket. The server applies
   * the order changes and cascades to all assigned customers — so this
   * call is genuinely customer-visible work, not a draft.
   */
  async saveCatalogueOrder(
    snapshotId: string,
    payload: {
      hardware?: Array<{ id: string; displayOrder: number }>;
      software?: Array<{ id: string; displayOrder: number }>;
      consulting?: Array<{ id: string; displayOrder: number }>;
    },
  ): Promise<{ success: boolean; count: number }> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    const client = await getMarketplaceClient();
    const response = await client.patch(
      `/catalog/partner/snapshots/${encodeURIComponent(snapshotId)}/order`,
      payload,
    );
    const data = response.data || {};
    return {
      success: Boolean(data.success),
      count: typeof data.count === 'number' ? data.count : 0,
    };
  }

  /**
   * Generate a product description with AI. For distributor SKUs with an
   * existing per-SKU enrichment, the server returns the canonical
   * marketingSummary instantly (zero LLM cost, partner-consistent). For
   * manual products, the server runs the enrichment agent against the
   * supplied edit-form fields and returns the generated summary.
   */
  async generateProductDescription(input: {
    productId?: string;
    distributorId?: string;
    sku?: string;
    name: string;
    vendor?: string;
    domain?: string;
    features?: string[];
    currentDescription?: string;
  }): Promise<{ description: string; source: 'enrichment' | 'generated' }> {
    if (!MARKETPLACE_ENABLED) throw new Error('MARKETPLACE_DISABLED');
    const client = await getMarketplaceClient();
    const response = await client.post('/partner/products/ai/description', input);
    const data = response.data || {};
    if (typeof data.description !== 'string' || !data.description.trim()) {
      throw new Error(data.error || 'No description was generated.');
    }
    return {
      description: data.description as string,
      source: (data.source === 'enrichment' ? 'enrichment' : 'generated') as 'enrichment' | 'generated',
    };
  }
}

// Export singleton instance
export const marketplaceService = new MarketplaceService();
export default marketplaceService;
